import { useAuth } from "@/app/providers";
import { routePageNameMap } from "@/libs/routePageNameMap";
import { CollectionsE2EPage } from "@/pages/CollectionsE2EPage";
import { CollectionsErrorPatternsPage } from "@/pages/CollectionsErrorPatternsPage";
import { CollectionsErrorsPage } from "@/pages/CollectionsErrorsPage";
import { CollectionsSizePage } from "@/pages/CollectionsSizePage";
import { CollectionsStatusPage } from "@/pages/CollectionsStatusPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { InfoMediaApiVersionPage } from "@/pages/InfoMediaApiVersionPage";
import { InfoMediaVersionUpdateAlertSettingsPage } from "@/pages/InfoMediaVersionUpdateAlertSettingsPage";
import { InfoTrendDataManagementPage } from "@/pages/InfoTrendDataManagementPage";
import { InfoWorkspaceSearchPage } from "@/pages/InfoWorkspaceSearchPage";
import { LoginPage } from "@/pages/LoginPage";
import { OrganizationOffboardingPage } from "@/pages/OrganizationOffboardingPage";
import { OrganizationOnboardingPage } from "@/pages/OrganizationOnboardingPage";
import { PreferencesAnomalyDetectionRulesPage } from "@/pages/PreferencesAnomalyDetectionRulesPage";
import { PreferencesChannelCollectionDelayPage } from "@/pages/PreferencesChannelCollectionDelayPage";
import { PreferencesCollectionSettingsPage } from "@/pages/PreferencesCollectionSettingsPage";
import { PreferencesDataForwarderPolicyPage } from "@/pages/PreferencesDataForwarderPolicyPage";
import { PreferencesFacebookResourceDatePage } from "@/pages/PreferencesFacebookResourceDatePage";
import { PreferencesUniversalThrottlingPolicyPage } from "@/pages/PreferencesUniversalThrottlingPolicyPage";
import { RecoverAppsflyerRawFileDeletePage } from "@/pages/RecoverAppsflyerRawFileDeletePage";
import { RecoverDataForwarderReprocessPage } from "@/pages/RecoverDataForwarderReprocessPage";
import { TokensTmsTokenManagementPage } from "@/pages/TokensTmsTokenManagementPage";
import { UserListPage } from "@/pages/UserListPage";
import { UsersPermissionGroupsPage } from "@/pages/UsersPermissionGroupsPage";
import { EmptyState, FullPageLoading } from "@/shared/components/feedback";
import { AppLayout } from "@/shared/components/layout/AppLayout";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

// eslint-disable-next-line react-refresh/only-export-components
export function useCurrentPageAccess() {
  const { pathname } = useLocation();
  const { canReadPage, canWritePage } = useAuth();

  const pageName = routePageNameMap[pathname as keyof typeof routePageNameMap] ?? null;

  return {
    pageName,
    canReadCurrentPage: pageName ? canReadPage(pageName) : true,
    canWriteCurrentPage: pageName ? canWritePage(pageName) : false,
  };
}

function RequirePageAccess({ children }: { children: React.ReactNode }) {
  const { canReadCurrentPage } = useCurrentPageAccess();
  if (!canReadCurrentPage) {
    return (
      <EmptyState
        title="권한이 없는 페이지입니다"
        description="해당 페이지에 접근할 수 있는 권한이 없습니다. 관리자에게 권한을 요청해주세요."
      />
    );
  }

  return <>{children}</>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <FullPageLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <FullPageLoading />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        {/* Protected routes with layout */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route
            index
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="collections/status"
            element={
              <RequirePageAccess>
                <CollectionsStatusPage />
              </RequirePageAccess>
            }
          />
          <Route
            path="collections/errors"
            element={
              <RequirePageAccess>
                <CollectionsErrorsPage />
              </RequirePageAccess>
            }
          />
          <Route
            path="collections/errors/patterns"
            element={
              <RequirePageAccess>
                <CollectionsErrorPatternsPage />
              </RequirePageAccess>
            }
          />
          <Route
            path="collections/size"
            element={
              <RequirePageAccess>
                <CollectionsSizePage />
              </RequirePageAccess>
            }
          />
          <Route
            path="collections/e2e-dashboard"
            element={
              <RequirePageAccess>
                <CollectionsE2EPage />
              </RequirePageAccess>
            }
          />

          <Route
            path="preferences/collection"
            element={
              <RequirePageAccess>
                <PreferencesCollectionSettingsPage />
              </RequirePageAccess>
            }
          />
          <Route
            path="preferences/universal-throttling"
            element={
              <RequirePageAccess>
                <PreferencesUniversalThrottlingPolicyPage />
              </RequirePageAccess>
            }
          />
          <Route
            path="preferences/anomaly-detection-rules"
            element={
              <RequirePageAccess>
                <PreferencesAnomalyDetectionRulesPage />
              </RequirePageAccess>
            }
          />
          <Route
            path="preferences/data-forwarder-policy"
            element={
              <RequirePageAccess>
                <PreferencesDataForwarderPolicyPage />
              </RequirePageAccess>
            }
          />
          <Route
            path="preferences/facebook-resource-date"
            element={
              <RequirePageAccess>
                <PreferencesFacebookResourceDatePage />
              </RequirePageAccess>
            }
          />
          <Route
            path="preferences/channel-collection-delay"
            element={
              <RequirePageAccess>
                <PreferencesChannelCollectionDelayPage />
              </RequirePageAccess>
            }
          />

          <Route
            path="recover/data-forwarder-reprocess"
            element={
              <RequirePageAccess>
                <RecoverDataForwarderReprocessPage />
              </RequirePageAccess>
            }
          />
          <Route
            path="recover/appsflyer-raw-file-delete"
            element={
              <RequirePageAccess>
                <RecoverAppsflyerRawFileDeletePage />
              </RequirePageAccess>
            }
          />

          <Route
            path="organization/onboarding"
            element={
              <RequirePageAccess>
                <OrganizationOnboardingPage />
              </RequirePageAccess>
            }
          />
          <Route
            path="organization/offboarding"
            element={
              <RequirePageAccess>
                <OrganizationOffboardingPage />
              </RequirePageAccess>
            }
          />

          <Route
            path="tokens/tms"
            element={
              <RequirePageAccess>
                <TokensTmsTokenManagementPage />
              </RequirePageAccess>
            }
          />

          <Route
            path="users/permission-groups"
            element={
              <RequirePageAccess>
                <UsersPermissionGroupsPage />
              </RequirePageAccess>
            }
          />

          <Route
            path="info/media-api-version"
            element={
              <RequirePageAccess>
                <InfoMediaApiVersionPage />
              </RequirePageAccess>
            }
          />
          <Route
            path="info/workspace-search"
            element={
              <RequirePageAccess>
                <InfoWorkspaceSearchPage />
              </RequirePageAccess>
            }
          />
          <Route
            path="info/media-version-update-alert-settings"
            element={
              <RequirePageAccess>
                <InfoMediaVersionUpdateAlertSettingsPage />
              </RequirePageAccess>
            }
          />
          <Route
            path="info/trend-data-management"
            element={
              <RequirePageAccess>
                <InfoTrendDataManagementPage />
              </RequirePageAccess>
            }
          />

          <Route
            path="users"
            element={
              <RequirePageAccess>
                <UserListPage />
              </RequirePageAccess>
            }
          />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
